import { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { Avatar, Box, Card, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import AccordionConnectionPoints from './components/AccordionConnectionPoints';
import AccordionHighlights from './components/AccordionHighlights';
import AccordionThematicGroups from './components/AccordionThematicGroups';
import SearchBar from './components/SearchBar';
import UserActionsMenu from './components/UserActionsMenu';
import UserCard from './components/UserCard';
import {
  useCompletionByUser,
  useConnectionPoints,
  useMatchByCommonNeighbors,
  useOrganizationUsers,
  useUserAuthProfile,
  useUserDetails,
} from './hooks';
import { useUserConnections } from './hooks/useUserConnections';
import { useUserProfileImage } from './hooks/useUserProfileImage';
import { useUserRelationship } from './hooks/useUserRelationship';
import { useUserStats } from './hooks/useUserStats';
import { UserToOrganization } from './types';
import bgBio from 'assets/images/network/bg_bio.png';
import Spin from 'components/Spin';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import { apiNetworking } from 'services/apiService';

const IndividualBio = () => {
  const theme = useThemeContext();
  const muiTheme = useTheme();
  const { toastSuccess, toastError } = useToast();

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { user } = useUser();
  const location = useLocation();
  const [selectedMember, setSelectedMember] = useState<UserToOrganization | null>(null);
  const { selectMenuByDocumentId } = useMenuSelection();

  const { userDetails, loading: loadingUserDetails } = useUserDetails(
    selectedMember?.user.id || ''
  );
  const { userProfile, loading: loadingUserAuthProfile } = useUserAuthProfile(
    selectedMember?.user.id || ''
  );
  const { users: organizationUsers, loading: loadingOrganizationUsers } = useOrganizationUsers();
  const { imageUrl, loading: loadingImage } = useUserProfileImage(selectedMember?.user.id || '');

  const {
    stats: userStats,
    loading: loadingStats,
    setLoading: setLoadingStats,
    setStats: setUserStats,
  } = useUserStats(selectedMember?.user.id || '');

  // Hooks para validação de conexão e relacionamento
  const { connections, loading: loadingConnections } = useUserConnections(
    selectedMember?.user.id || '',
    user?.id || ''
  );
  const { relationship, loading: loadingRelationship } = useUserRelationship(
    user?.id || '',
    selectedMember?.user.id || ''
  );

  // Hooks de análise com controle manual
  const {
    data: connectionPointsReport,
    loading: loadingConnectionPointsReport,
    error: connectionPointsReportError,
    executeRequest: executeConnectionPoints,
  } = useConnectionPoints(user?.id || '', selectedMember?.user.id || '');

  const {
    data: completionByUser,
    loading: loadingCompletionByUser,
    error: completionByUserError,
    executeRequest: executeCompletionByUser,
  } = useCompletionByUser(selectedMember?.user.id || '');

  const {
    data: matchByCommonNeighbors,
    loading: loadingMatchByCommonNeighbors,
    error: matchByCommonNeighborsError,
    executeRequest: executeMatchByCommonNeighbors,
  } = useMatchByCommonNeighbors('form-id-exemplo', selectedMember?.user.id || '');

  // Restaura o contexto do menu quando recebido via location.state
  useEffect(() => {
    if (location.state?.menuContext) {
      const { documentId, path, preserveLevel } = location.state.menuContext;
      console.log('[IndividualBio] Restaurando contexto do menu:', {
        documentId,
        path,
        preserveLevel,
        currentPath: location.pathname,
      });

      // Se preserveLevel é true, mantém o nível atual
      if (preserveLevel) {
        selectMenuByDocumentId(documentId, false, undefined, path, true);
      } else {
        selectMenuByDocumentId(documentId, false, undefined, path);
      }
    }
  }, [location.state?.menuContext, selectMenuByDocumentId]);

  useEffect(() => {
    if (organizationUsers.length && (location.state?.selectedUser || user?.id)) {
      const memberObj = organizationUsers.find(
        u => u.user.id === (location.state?.selectedUser || user?.id)
      );
      setSelectedMember(memberObj || null);
    }
  }, [organizationUsers, location.state, user]);

  // Função para executar análises
  const handleExecuteAnalysis = (type: string) => {
    console.log('[IndividualBio] Executando análise:', type);

    switch (type) {
      case 'connection-points':
        executeConnectionPoints();
        break;
      case 'completion-by-user':
        executeCompletionByUser();
        break;
      case 'match-by-common-neighbors':
        executeMatchByCommonNeighbors();
        break;
      default:
        console.warn('[IndividualBio] Tipo de análise não suportado:', type);
    }
  };

  // Estados para controlar os botões
  const loadingStates = {
    'connection-points': loadingConnectionPointsReport,
    'completion-by-user': loadingCompletionByUser,
    'match-by-common-neighbors': loadingMatchByCommonNeighbors,
  };

  const errorStates = {
    'connection-points': connectionPointsReportError,
    'completion-by-user': completionByUserError,
    'match-by-common-neighbors': matchByCommonNeighborsError,
  };

  const dataStates = {
    'connection-points': connectionPointsReport,
    'completion-by-user': completionByUser,
    'match-by-common-neighbors': matchByCommonNeighbors,
  };

  if (loadingUserDetails || loadingUserAuthProfile || loadingOrganizationUsers || loadingImage) {
    return <Spin centerHorizontally centerVertically />;
  }

  const handleRatingChange = (newValue: number) => {
    // Validações para rating
    if (!selectedMember || !user) {
      toastError('Erro: dados do usuário não encontrados');
      return;
    }

    const isOwnProfile = user.id === selectedMember.user.id;
    if (isOwnProfile) {
      toastError('Você não pode avaliar seu próprio perfil');
      return;
    }

    // Verificar se são conexões usando os dados da API
    const isConnected = connections?.level_code === 'CONNECTION';
    if (!isConnected) {
      toastError('Necessário ser uma conexão para avaliar o usuário');
      return;
    }

    // Verificar se o relacionamento está ativo
    const isRelationshipActive = relationship?.status === 'ACCEPTED';
    if (!isRelationshipActive) {
      toastError('Necessário ter um relacionamento ativo para avaliar o usuário');
      return;
    }

    setLoadingStats(true);
    postEdge('RATED', newValue.toString());
    setUserStats(prev => (prev ? { ...prev, nps: newValue } : null));
    setLoadingStats(false);
  };

  const postEdge = async (type_code: string, value: string | null = null) => {
    // setIsLoadingPost(true);
    try {
      const from_user_uuid = user?.id;
      const to_user_uuid = selectedMember?.user.id || '';

      const data = {
        from_user_uuid,
        to_user_uuid,
        type_code,
        value,
      };

      await apiNetworking.post('/edge/create', data);

      toastSuccess('Dados salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar aresta:', error);
      toastError('Erro ao salvar informação. Contate o suporte.');
    } //finally {
    //   setIsLoadingPost(false);
    // }
  };

  return (
    <Box p={0} width="100%">
      <Box p={2} bgcolor="background.paper" borderRadius={2} mb={2}>
        <SearchBar
          users={organizationUsers.filter((u: any) => u.user.id !== user?.id)}
          value={selectedMember}
          onSelectUser={uuid => {
            const memberObj = organizationUsers.find(u => u.user.id === uuid);
            setSelectedMember(memberObj || null);
          }}
          loading={loadingOrganizationUsers}
          loggedUserId={user?.id || ''}
        />
      </Box>

      {selectedMember && (
        <Card
          sx={{
            height: isMobile ? 'auto' : '100%',
            borderRadius: 2,
            backgroundImage: `url(${bgBio})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              bgcolor: theme.background.primary,
              opacity: 0.4,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />

          <Stack
            sx={{ position: 'relative', zIndex: 2 }}
            justifyContent="space-between"
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 2 : 0}
            width="100%"
          >
            <Stack width="100%" p={1} spacing={2} direction="column" alignItems="center">
              {selectedMember && userDetails && userProfile && (
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  gap={isMobile ? 2 : 4}
                  alignItems={isMobile ? 'flex-start' : 'flex-start'}
                  width="100%"
                  paddingY={1}
                >
                  <Box
                    alignItems="start"
                    justifyContent="start"
                    display="flex"
                    width={isMobile ? 1 : 'auto'}
                  >
                    <Avatar
                      src={imageUrl ?? undefined}
                      alt={userDetails?.first_name}
                      sx={{
                        width: isMobile ? 160 : 240,
                        height: isMobile ? 160 : 240,
                        margin: isMobile ? '0 auto' : 0,
                      }}
                    />
                  </Box>

                  <UserCard
                    userDetails={userDetails}
                    userProfile={userProfile}
                    loadingDetails={loadingUserDetails}
                    loadingProfile={loadingUserAuthProfile}
                    userStats={userStats}
                    loadingStats={loadingStats}
                    onRatingChange={handleRatingChange}
                    loggedUserId={user?.id || ''}
                    selectedUserId={selectedMember.user.id}
                  />
                </Stack>
              )}
            </Stack>

            <Box
              display="flex"
              justifyContent={isMobile ? 'center' : 'flex-end'}
              alignItems="center"
            >
              {selectedMember && (
                <UserActionsMenu
                  loggedUserId={user?.id || ''}
                  selectedUserId={selectedMember.user.id}
                  hostName={`${selectedMember.user.first_name} ${selectedMember.user.last_name}`}
                />
              )}
            </Box>
          </Stack>
        </Card>
      )}

      {!selectedMember && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="start"
          width="100%"
          height="100%"
          p={2}
        >
          <Typography color="text.secondary" mt={3}>
            Nenhum membro selecionado.
          </Typography>
        </Box>
      )}

      <Stack width={1} spacing={isMobile ? 1 : 2} mt={isMobile ? 1 : 2}>
        {selectedMember && (
          <>
            <AccordionHighlights
              userId={selectedMember.user.id}
              loggedUserId={user?.id || ''}
              stats={userStats}
              loadingStats={loadingStats}
            />
            <AccordionConnectionPoints
              fromUserId={user?.id || ''}
              toUserId={selectedMember.user.id}
            />
            <AccordionThematicGroups
              userId={selectedMember.user.id}
              loggedUserId={user?.id || ''}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default IndividualBio;
